import React from 'react'
import { useQuery } from '@apollo/client'
import { gql } from '~/__generated__'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '~/ui/card'
import { Button } from '~/ui/button'
import { Video, Music } from 'lucide-react'
import { useToast } from '~/hooks/use-toast'
import { useSafeMutation } from '~/common/use-safe-mutation'

const HARDWARE_PRODUCTS_QUERY = gql(`
  query HardwareProducts {
    hardwareProducts {
      id
      name
      productType
      priceCents
      description
      stripePriceId
      active
    }
  }
`)

const CREATE_HARDWARE_CHECKOUT_SESSION_MUTATION = gql(`
  mutation CreateHardwareCheckoutSession($input: CreateHardwareCheckoutSessionInput!) {
    createHardwareCheckoutSession(input: $input) {
      checkoutSession {
        url
        sessionId
      }
      errors
    }
  }
`)

export const HardwareProducts: React.FC = () => {
  const { toast } = useToast()
  const { data, loading } = useQuery(HARDWARE_PRODUCTS_QUERY)
  const [createCheckoutSession] = useSafeMutation(CREATE_HARDWARE_CHECKOUT_SESSION_MUTATION)

  const products = data?.hardwareProducts || []

  const handlePurchase = async (stripePriceId: string) => {
    const successUrl = `${window.location.origin}/billing?success=true`
    const cancelUrl = `${window.location.origin}/billing?canceled=true`

    const result = await createCheckoutSession({
      variables: {
        input: {
          stripePriceId,
          productType: 'audio',
          quantity: 1,
          successUrl,
          cancelUrl,
          shippingAddress: null,
        },
      },
    })

    if (result.data?.createHardwareCheckoutSession?.checkoutSession?.url) {
      window.location.href = result.data.createHardwareCheckoutSession.checkoutSession.url
    } else {
      const errors = result.data?.createHardwareCheckoutSession?.errors || ['Failed to create checkout session']
      toast({
        title: 'Error',
        description: errors.join(', '),
        variant: 'destructive',
      })
    }
  }

  const getIcon = (productType: string) => {
    return productType === 'audio' ? Music : Video
  }

  if (loading) {
    return <div className="text-center py-8">Loading hardware products...</div>
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Hardware Products</CardTitle>
          <CardDescription>Purchase hardware players for your zones</CardDescription>
        </CardHeader>
        <CardContent>
          {products.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No hardware products available</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {products.map((product: any) => {
                const Icon = getIcon(product.productType)
                return (
                  <Card key={product.id}>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Icon className="h-6 w-6" />
                        <CardTitle>{product.name}</CardTitle>
                      </div>
                      <CardDescription>{product.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        ${(product.priceCents / 100).toFixed(2)}
                      </div>
                    </CardContent>
                    <CardFooter>
                      <Button
                        className="w-full"
                        onClick={() => handlePurchase(product.stripePriceId)}
                        disabled={!product.stripePriceId}
                      >
                        Purchase
                      </Button>
                    </CardFooter>
                  </Card>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
